import {invalidateOnSuccess, metadaApi} from '@dms/api/base';

import {
  GetCoreExchangeRequest,
  GetCoreExchangeRequestSchema,
  GetCoreExchangeResponse,
  GetCoreExchangeResponseSchema,
  PatchCoreExchangeCancelDeliveryRequest,
  PatchCoreExchangeCancelDeliveryRequestSchema,
  PatchCoreExchangeCustomerDeliveryRequest,
  PatchCoreExchangeCustomerDeliveryRequestSchema,
  PatchCoreExchangeGeneralSettingsRequest,
  PatchCoreExchangeGeneralSettingsRequestSchema,
  PatchCoreExchangeInspectionRequest,
  PatchCoreExchangeInspectionRequestSchema,
  PatchCoreExchangeResponse,
  PatchCoreExchangeResponseSchema,
  PatchCoreExchangeRmaAuthorizationRequest,
  PatchCoreExchangeRmaAuthorizationRequestSchema,
  PatchCoreExchangeRmaDenialRequest,
  PatchCoreExchangeRmaDenialRequestSchema,
  PatchCoreExchangeRmaRequestRequest,
  PatchCoreExchangeRmaRequestRequestSchema,
  PatchCoreExchangeSettingsRequest,
  PatchCoreExchangeSettingsRequestSchema,
  PatchCoreExchangeWarehouseReceiptRequest,
  PatchCoreExchangeWarehouseReceiptRequestSchema,
  PostCoreExchangeRequest,
  PostCoreExchangeRequestSchema,
  PostCoreExchangeResponse,
  PostCoreExchangeResponseSchema,
} from './types';

const coreExchangeUrl = (coreExchangeId: string, action: string) =>
  `warehouse/v1/core-exchange/${coreExchangeId}/${action}`;

export const metadaWarehouseCoreExchangeApi = metadaApi.injectEndpoints({
  endpoints: (build) => ({
    getCoreExchange: build.query<GetCoreExchangeResponse, GetCoreExchangeRequest>({
      query: (queryArg) => ({
        url: `warehouse/v1/core-exchange/${queryArg.coreExchangeId}`,
        method: 'GET',
        queryArg,
      }),
      extraOptions: {
        requestSchema: GetCoreExchangeRequestSchema,
        responseSchema: GetCoreExchangeResponseSchema,
      },
    }),
    // No cached query lists core exchanges yet and the caller navigates to the new detail, so
    // there is nothing to invalidate.
    postCoreExchange: build.mutation<PostCoreExchangeResponse, PostCoreExchangeRequest>({
      query: (body) => ({
        url: `warehouse/v1/core-exchange`,
        method: 'POST',
        body,
      }),
      extraOptions: {
        requestSchema: PostCoreExchangeRequestSchema,
        responseSchema: PostCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeGeneralSettings: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeGeneralSettingsRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'general-settings'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeGeneralSettingsRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeCancelDelivery: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeCancelDeliveryRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'cancel-delivery'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeCancelDeliveryRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeCustomerDelivery: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeCustomerDeliveryRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'customer-delivery'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeCustomerDeliveryRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeInspection: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeInspectionRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'inspection'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeInspectionRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeWarehouseReceipt: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeWarehouseReceiptRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'warehouse-receipt'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeWarehouseReceiptRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeRmaRequest: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeRmaRequestRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'rma-request'),
        method: 'PATCH',
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeRmaRequestRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeRmaAuthorization: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeRmaAuthorizationRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'rma-authorization'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeRmaAuthorizationRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeRmaDenial: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeRmaDenialRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'rma-denial'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeRmaDenialRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
    patchCoreExchangeSettings: build.mutation<
      PatchCoreExchangeResponse,
      PatchCoreExchangeSettingsRequest
    >({
      query: (queryArg) => ({
        url: coreExchangeUrl(queryArg.coreExchangeId, 'settings'),
        method: 'PATCH',
        body: queryArg.body,
        queryArg,
      }),
      invalidatesTags: invalidateOnSuccess(['headerDynamicActions']),
      extraOptions: {
        requestSchema: PatchCoreExchangeSettingsRequestSchema,
        responseSchema: PatchCoreExchangeResponseSchema,
      },
    }),
  }),
});

export const {
  useGetCoreExchangeQuery,
  usePostCoreExchangeMutation,
  usePatchCoreExchangeGeneralSettingsMutation,
  usePatchCoreExchangeCancelDeliveryMutation,
  usePatchCoreExchangeCustomerDeliveryMutation,
  usePatchCoreExchangeInspectionMutation,
  usePatchCoreExchangeWarehouseReceiptMutation,
  usePatchCoreExchangeRmaRequestMutation,
  usePatchCoreExchangeRmaAuthorizationMutation,
  usePatchCoreExchangeRmaDenialMutation,
  usePatchCoreExchangeSettingsMutation,
} = metadaWarehouseCoreExchangeApi;
